/**
 * A quest step's objectives and rewards (ADR-139 decisions 3 and 4): two closed tagged sets, each entry an object
 * whose `kind` picks its fields. A kind the design reserves is refused naming the row that builds it. Pure.
 */

import { MAP_ID_PATTERN } from "daedalus-core";
import { NODE_ID_PATTERN } from "../content/dialogue.ts";
import { SLUG_PATTERN } from "../content/slug.ts";
import { ITEM_ID_PATTERN } from "../rules/types.ts";

/**
 * The objective kinds a step may use today.
 */
export const OBJECTIVE_KINDS = Object.freeze(["talk", "kill", "collect", "reach", "deliver"] as const);

/**
 * An objective kind built today.
 */
export type ObjectiveKind = (typeof OBJECTIVE_KINDS)[number];

/**
 * The objective kinds the design reserves, each with the row or design that opens it.
 */
export const RESERVED_OBJECTIVE_KINDS: { readonly [kind: string]: string } = Object.freeze({
  offer: "P2.23-T9",
  learn: "P2.23-T10",
  gather: "the gathering design",
  craft: "the making-things design",
  duel: "the duels design",
  escort: "a later row",
});

/**
 * The reward kinds a step may give today.
 */
export const REWARD_KINDS = Object.freeze(["item", "coin", "xp", "fact"] as const);

/**
 * A reward kind built today.
 */
export type RewardKind = (typeof REWARD_KINDS)[number];

/**
 * The reward kinds the design reserves until a writer needs them: a form learned directly and a choice of rewards.
 */
export const RESERVED_REWARD_KINDS = Object.freeze(["form", "choose"] as const);

/**
 * The fact kinds a `fact` reward may write (decision 4): content-written unlocks for later quests and doors.
 */
export const REWARD_FACT_KINDS = Object.freeze(["moment", "reached"] as const);

/**
 * The fewest a `kill`, `collect` or `deliver` counts.
 */
export const MIN_OBJECTIVE_COUNT = 1;

/**
 * The most a `kill`, `collect` or `deliver` counts.
 */
export const MAX_OBJECTIVE_COUNT = 99;

/**
 * The most creature templates one `kill` names.
 */
export const MAX_KILL_CREATURES = 8;

/**
 * The most items one `item` reward gives.
 */
export const MAX_REWARD_ITEMS = 99;

/**
 * The most coin or XP one reward gives.
 */
export const MAX_REWARD_AMOUNT = 1_000_000;

/**
 * A `quest_point` marker's place id, which a `reach` names.
 */
export const PLACE_ID_PATTERN = /^[a-z0-9_]{1,48}$/;

/**
 * A fact name a fact reward writes: its kind, a colon and the name (ADR-120 decision 1.3).
 */
export const REWARD_FACT_PATTERN = /^(moment|reached):[a-z0-9_.]{1,64}$/;

/**
 * An objective of a step.
 */
export type QuestObjective =
  | { readonly kind: "talk"; readonly npc: string; readonly node?: string }
  | { readonly kind: "kill"; readonly creature: string | readonly string[]; readonly count: number; readonly map?: string }
  | { readonly kind: "collect"; readonly item: string; readonly count: number }
  | { readonly kind: "reach"; readonly place: string }
  | { readonly kind: "deliver"; readonly item: string; readonly count: number; readonly npc: string };

/**
 * A reward a step gives when it is done.
 */
export type QuestReward =
  | { readonly kind: "item"; readonly item: string; readonly count: number }
  | { readonly kind: "coin"; readonly amount: number }
  | { readonly kind: "xp"; readonly amount: number }
  | { readonly kind: "fact"; readonly name: string };

/**
 * Records a problem at a path inside a step.
 */
export type Report = (field: string, message: string) => void;

/**
 * A parsed JSON object.
 */
type Plain = { readonly [key: string]: unknown };

/**
 * How one field of an entry is checked: whether it may be left out and the test with its sentence.
 */
interface FieldRule {
  readonly optional?: true;
  readonly check: (value: unknown, report: Report, path: string) => void;
}

/**
 * Whether a value is a plain object.
 *
 * @param value Anything.
 * @returns True for an object that is not null or an array.
 */
export function isPlain(value: unknown): value is Plain {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * A rule for a string field matching a pattern.
 *
 * @param pattern The pattern.
 * @param what What the field names, for the sentence.
 * @param optional Whether it may be left out.
 * @returns The rule.
 */
function idRule(pattern: RegExp, what: string, optional?: true): FieldRule {
  return {
    ...(optional === undefined ? {} : { optional }),
    check: (value, report, path) => {
      if (typeof value !== "string" || !pattern.test(value)) {
        report(path, `must name ${what}`);
      }
    },
  };
}

/**
 * A rule for a whole number within a range.
 *
 * @param min The least.
 * @param max The most.
 * @returns The rule.
 */
function wholeRule(min: number, max: number): FieldRule {
  return {
    check: (value, report, path) => {
      if (typeof value !== "number" || !Number.isInteger(value) || value < min || value > max) {
        report(path, `must be a whole number from ${min} to ${max}`);
      }
    },
  };
}

/**
 * The rule of an objective's `count`.
 */
const COUNT = wholeRule(MIN_OBJECTIVE_COUNT, MAX_OBJECTIVE_COUNT);

/**
 * The rule of a field naming an NPC template.
 */
const NPC = idRule(SLUG_PATTERN, "an NPC template: lower-case letters, digits and -");
/**
 * The rule of a field naming an item.
 */
const ITEM = idRule(ITEM_ID_PATTERN, "an item: lower-case letters, digits and _");
/**
 * The rule of a coin or XP reward's `amount`.
 */
const AMOUNT = wholeRule(1, MAX_REWARD_AMOUNT);

/**
 * A `kill`'s creature: one template id or a list of 1 to 8.
 */
const CREATURE: FieldRule = {
  check: (value, report, path) => {
    const message = "must name a creature template: lower-case letters, digits and -";
    if (typeof value === "string") {
      if (!SLUG_PATTERN.test(value)) {
        report(path, message);
      }
      return;
    }
    if (!Array.isArray(value) || value.length < 1 || value.length > MAX_KILL_CREATURES) {
      report(path, `must be a creature template or a list of 1 to ${MAX_KILL_CREATURES}`);
      return;
    }
    value.forEach((one, at) => {
      if (typeof one !== "string" || !SLUG_PATTERN.test(one)) {
        report(`${path}[${at}]`, message);
      }
    });
  },
};

/**
 * Each objective kind's field rules.
 */
const OBJECTIVE_FIELDS: { readonly [kind in ObjectiveKind]: { readonly [field: string]: FieldRule } } = {
  talk: { npc: NPC, node: idRule(NODE_ID_PATTERN, "a dialogue node: lower case, digits, _ and -", true) },
  kill: { creature: CREATURE, count: COUNT, map: idRule(MAP_ID_PATTERN, "a map: letters, digits, ., _ and -", true) },
  collect: { item: ITEM, count: COUNT },
  reach: { place: idRule(PLACE_ID_PATTERN, "a quest point's place: lower case, digits and _") },
  deliver: { item: ITEM, count: COUNT, npc: NPC },
};

/**
 * Each reward kind's field rules.
 */
const REWARD_FIELDS: { readonly [kind in RewardKind]: { readonly [field: string]: FieldRule } } = {
  item: { item: ITEM, count: wholeRule(1, MAX_REWARD_ITEMS) },
  coin: { amount: AMOUNT },
  xp: { amount: AMOUNT },
  fact: { name: idRule(REWARD_FACT_PATTERN, `a fact of kind ${REWARD_FACT_KINDS.join(" or ")}, such as moment:heard_of_university`) },
};

/**
 * Checks an entry's fields against its kind's rules: every field known, every required one present.
 *
 * @param entry The entry.
 * @param rules The kind's field rules.
 * @param report Records a problem.
 * @param path The entry's path.
 */
function checkFields(entry: Plain, rules: { readonly [field: string]: FieldRule }, report: Report, path: string): void {
  for (const key of Object.keys(entry)) {
    if (key !== "kind" && !Object.hasOwn(rules, key)) {
      report(`${path}.${key}`, `${key} is not a field of a ${String(entry["kind"])}`);
    }
  }
  for (const [field, rule] of Object.entries(rules)) {
    const value = entry[field];
    if (value === undefined) {
      if (rule.optional === undefined) {
        report(`${path}.${field}`, `a ${String(entry["kind"])} must have ${field}`);
      }
      continue;
    }
    rule.check(value, report, `${path}.${field}`);
  }
}

/**
 * Checks one objective: an object of a kind built today, with that kind's fields.
 *
 * @param value The objective as read.
 * @param report Records a problem inside the step.
 * @param path The objective's path.
 */
export function checkObjective(value: unknown, report: Report, path: string): void {
  if (!isPlain(value)) {
    report(path, "an objective must be an object with a kind");
    return;
  }
  const kind = value["kind"];
  if (typeof kind === "string" && Object.hasOwn(RESERVED_OBJECTIVE_KINDS, kind)) {
    report(`${path}.kind`, `the ${kind} objective is reserved and refused until ${RESERVED_OBJECTIVE_KINDS[kind] as string} builds it`);
  } else if (typeof kind !== "string" || !(OBJECTIVE_KINDS as readonly string[]).includes(kind)) {
    report(`${path}.kind`, `kind must be ${OBJECTIVE_KINDS.join(", ")}`);
  } else {
    checkFields(value, OBJECTIVE_FIELDS[kind as ObjectiveKind], report, path);
  }
}

/**
 * Checks one reward: an object of a kind built today, with that kind's fields.
 *
 * @param value The reward as read.
 * @param report Records a problem inside the step.
 * @param path The reward's path.
 */
export function checkReward(value: unknown, report: Report, path: string): void {
  if (!isPlain(value)) {
    report(path, "a reward must be an object with a kind");
    return;
  }
  const kind = value["kind"];
  if (typeof kind === "string" && (RESERVED_REWARD_KINDS as readonly string[]).includes(kind)) {
    report(`${path}.kind`, `the ${kind} reward is reserved and refused until a writer needs it`);
  } else if (typeof kind !== "string" || !(REWARD_KINDS as readonly string[]).includes(kind)) {
    report(`${path}.kind`, `kind must be ${REWARD_KINDS.join(", ")}`);
  } else {
    checkFields(value, REWARD_FIELDS[kind as RewardKind], report, path);
  }
}
